import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { TierCards } from "../components/TierCards";
import { useAuth } from "../context/AuthContext";
import { api, formatError, startCheckout, TIER_LABEL } from "../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const fmt = (d) => (d ? format(new Date(d), "MMMM d, yyyy") : "—");

function PlanSummary({ b, busy, onCancel, onResume }) {
  return (
    <div data-testid="billing-summary" className="border border-[#0E1B3D]/[0.08] bg-[#FFFFFF] p-6 sm:p-10 grid gap-8 md:grid-cols-[1fr_auto] md:items-end">
      <div>
        <p className="font-mono-x text-[0.65rem] tracking-[0.22em] uppercase text-[#4A5878]">Current plan</p>
        <p data-testid="billing-current-tier" className="mt-3 font-display text-4xl sm:text-5xl text-[#0E1B3D]">
          {b.tier_name || "No tier"} {b.price != null && <span className="text-xl text-[#4A5878]">${b.price}/mo</span>}
        </p>
        <p data-testid="billing-renewal-text" className="mt-3 text-sm text-[#26355A]">
          {!b.has_subscription ? "No active Stripe subscription on this account."
            : b.cancel_at_period_end ? <>Cancelled — access ends on <span className="text-[#D7263D]">{fmt(b.current_period_end)}</span>.</>
            : <>Renews on <span className="text-[#D7263D]">{fmt(b.current_period_end)}</span>.</>}
        </p>
      </div>
      {b.has_subscription && (b.cancel_at_period_end ? (
        <button data-testid="billing-resume-btn" disabled={busy} onClick={onResume} className="gold-btn px-7 py-4 text-xs font-semibold tracking-[0.16em] uppercase flex items-center gap-2 disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Keep my membership
        </button>
      ) : (
        <button data-testid="billing-cancel-btn" disabled={busy} onClick={onCancel} className="ghost-btn px-7 py-4 text-xs tracking-[0.16em] uppercase disabled:opacity-60">Cancel membership</button>
      ))}
    </div>
  );
}

function History({ payments }) {
  if (!payments?.length) return null;
  return (
    <div data-testid="billing-history" className="border border-[#0E1B3D]/[0.08] bg-[#FFFFFF]">
      <p className="px-6 py-4 border-b border-[#0E1B3D]/[0.08] font-mono-x text-[0.65rem] tracking-[0.22em] uppercase text-[#4A5878]">Payment history</p>
      {payments.map((p, i) => (
        <div key={i} data-testid="billing-history-row" className="px-6 py-4 flex justify-between gap-4 text-sm border-b border-[#0E1B3D]/[0.05] last:border-0">
          <span className="text-[#26355A]">{p.kind === "tip" ? "Tip" : `${TIER_LABEL[p.tier] || ""} membership`}</span>
          <span className="text-[#4A5878]">{fmt(p.created_at)}</span>
          <span className="text-[#D7263D]">${p.amount}</span>
        </div>
      ))}
    </div>
  );
}

export default function Billing() {
  const { refreshUser } = useAuth();
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(null);
  const [confirm, setConfirm] = useState(false);

  const load = useCallback(() => {
    api.get("/billing").then((r) => setB(r.data)).catch(() => setB({}));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const act = async (path, msg) => {
    setBusy(true);
    try {
      await api.post(path);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };

  const change = async (tier) => {
    setLoading(tier);
    try {
      if (!b?.has_subscription) return await startCheckout({ kind: "tier", tier });
      await api.post("/billing/change", { tier });
      await refreshUser();
      toast.success(`You're now on ${TIER_LABEL[tier]}`);
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(null);
    }
  };

  return (
    <main data-testid="billing-page" className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-[130px] pb-24 space-y-14">
      <div>
        <p className="eyebrow mb-5">Billing</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#0E1B3D]">Your <span className="italic text-[#D7263D]">membership</span></h1>
      </div>
      {b === null ? <div className="h-40 border border-[#0E1B3D]/[0.08] bg-[#FFFFFF] animate-pulse" /> : (
        <>
          <PlanSummary b={b} busy={busy} onCancel={() => setConfirm(true)} onResume={() => act("/billing/resume", "Membership resumed")} />
          <section>
            <h2 className="font-display text-2xl sm:text-3xl text-[#0E1B3D] mb-3">Change plan</h2>
            <p className="text-sm text-[#4A5878] mb-10 max-w-xl">Upgrades charge the price difference right away. Downgrades apply now and the unused amount is credited to your next bill.</p>
            <TierCards currentTier={b.tier} loading={loading} onSelect={change} allowSwitch />
          </section>
          <History payments={b.payments} />
        </>
      )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="billing-cancel-dialog" className="bg-[#FFFFFF] border-[#0E1B3D]/15">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl font-normal text-[#0E1B3D]">Cancel your membership?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#4A5878]">You keep access until {fmt(b?.current_period_end)}. You won't be charged again.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="billing-cancel-dismiss-btn" className="bg-transparent border-[#0E1B3D]/20 text-[#0E1B3D]">Keep it</AlertDialogCancel>
            <AlertDialogAction data-testid="billing-cancel-confirm-btn" onClick={() => act("/billing/cancel", "Membership will end at the period end")} className="bg-[#D7263D] hover:bg-[#B81D31] text-white">Cancel membership</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
